import React, { useEffect, useMemo, useState } from 'react';

import { useLocation, useParams } from 'react-router-dom';

import { fetchItemByQrCode, fetchLocationForQr, recordQrScan } from 'api/qr';
import {
  buildQrCodeUrl,
  getGoogleLocationIdForQr,
  isQrHost,
  isTemporaryGoogleQrCode,
  normalizeQrCodeUrl,
} from 'constants/qr';

import { QrRedirectProps } from './QrRedirect.props';

import QrRedirectView from './QrRedirect.view';

type Status = 'loading' | 'redirecting' | 'error';

const getVenueId = (currentVenue: unknown): string | undefined => {
  if (!currentVenue) {
    return undefined;
  }

  if (typeof currentVenue === 'string') {
    return currentVenue;
  }

  if (typeof currentVenue === 'object' && currentVenue !== null) {
    return (currentVenue as { _id?: string })._id;
  }

  return undefined;
};

const QrRedirectContainer = (_props: QrRedirectProps) => {
  const params = useParams();
  const location = useLocation();

  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState<string>();

  const qrCodeUrl = useMemo(() => {
    // On qr.rworldreuse.com/XXX the scanned URL is already the canonical value.
    if (isQrHost()) {
      return normalizeQrCodeUrl(
        `${window.location.origin}${location.pathname}`,
      );
    }

    // On the main app, /qr/:code is used for local / same-origin testing.
    const code = params.code || '';
    if (!code) {
      return '';
    }

    return buildQrCodeUrl(code);
  }, [location.pathname, params.code]);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      if (!qrCodeUrl) {
        setStatus('error');
        setMessage('Missing QR code in the URL.');
        return;
      }

      setStatus('loading');
      setMessage(undefined);

      const usesFixedLocation = isTemporaryGoogleQrCode(qrCodeUrl);

      try {
        let venueId: string | undefined;

        if (usesFixedLocation) {
          venueId = getGoogleLocationIdForQr();

          if (!venueId) {
            if (!cancelled) {
              setStatus('error');
              setMessage('This QR code could not be resolved.');
            }
            return;
          }
        } else {
          const item = await fetchItemByQrCode(qrCodeUrl);
          venueId = getVenueId(item?.currentVenue);

          if (!venueId) {
            if (!cancelled) {
              setStatus('error');
              setMessage('This item is not assigned to a venue.');
            }
            return;
          }
        }

        const locationDoc = await fetchLocationForQr(venueId);
        const landingUrl = locationDoc?.qrLandingUrl?.trim();

        if (!landingUrl) {
          if (!cancelled) {
            setStatus('error');
            setMessage(
              'This venue does not have a QR landing page configured.',
            );
          }
          return;
        }

        if (cancelled) {
          return;
        }

        try {
          await recordQrScan({ qrCode: qrCodeUrl, venueId });
        } catch {
          // A failed scan log should not block the landing page.
        }

        if (!cancelled) {
          setStatus('redirecting');
          // replace() so Back does not return to this interstitial.
          window.location.replace(landingUrl);
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
          setMessage(
            usesFixedLocation
              ? 'This venue could not be loaded.'
              : 'No item found for this QR code.',
          );
        }
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [qrCodeUrl]);

  return <QrRedirectView status={status} message={message} />;
};

export default QrRedirectContainer;
